import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { NAV } from "@/constants/testIds";

const links = [
  { to: "/", label: "Home", testId: NAV.home },
  { to: "/services", label: "How it Works", testId: NAV.services },
  { to: "/benefits", label: "Benefits", testId: NAV.benefits },
  { to: "/about", label: "About", testId: NAV.about },
  { to: "/contact", label: "Contact", testId: NAV.contact },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled
        ? "bg-white/85 backdrop-blur-xl border-b border-slate-200/70 shadow-[0_1px_0_rgba(15,23,42,0.04)]"
        : "bg-white/60 backdrop-blur-lg border-b border-transparent"
        }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" data-testid={NAV.logo} className="flex items-center gap-3 group">
            <img
              src="/assets/cascade-headerlogo-plus.png"
              alt=""
              aria-hidden="true"
              className="h-12 w-12 object-contain"
            />
            <img
              src="/assets/cascade-headerlogo-words.png"
              alt="Cascade Plus"
              className="h-4 w-auto max-w-[105px] object-contain"
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.testId}
                className={({ isActive }) =>
                  `relative px-4 py-2 text-sm font-semibold rounded-full transition-colors ${isActive
                    ? "text-[#0A2540] bg-slate-100"
                    : "text-slate-600 hover:text-[#0A2540] hover:bg-slate-50"
                  }`
                }
                end={l.to === "/"}
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <Link
              to="https://orderpad.cascadeplus.co.uk/"
              data-testid={NAV.loginButton}
              className="inline-flex items-center gap-2 text-slate-700 hover:text-[#0A2540] px-4 py-2.5 rounded-full text-sm font-semibold border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-all"
            >
              Log in
            </Link>
            <Link
              to="/request-access"
              data-testid={NAV.ctaButton}
              className="inline-flex items-center gap-2 bg-[#0A2540] text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-[#0F365C] transition-all hover:gap-3 shadow-sm"
            >
              Request Free Access
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <button
            data-testid={NAV.mobileToggle}
            aria-label="Toggle menu"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden p-2 rounded-lg text-[#0A2540] hover:bg-slate-100"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid={NAV.mobileMenu} className="lg:hidden bg-white border-t border-slate-200">
          <div className="px-4 py-4 flex flex-col gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`${l.testId}-mobile`}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-lg text-sm font-semibold ${isActive ? "bg-slate-100 text-[#0A2540]" : "text-slate-600 hover:bg-slate-50"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="https://orderpad.cascadeplus.co.uk/"
              data-testid={`${NAV.loginButton}-mobile`}
              className="mt-2 inline-flex items-center justify-center gap-2 border border-slate-200 text-[#0A2540] px-5 py-3 rounded-full text-sm font-semibold hover:bg-slate-50"
            >
              Log in
            </Link>
            <Link
              to="/request-access"
              className="inline-flex items-center justify-center gap-2 bg-[#0A2540] text-white px-5 py-3 rounded-full text-sm font-semibold"
            >
              Request Free Access
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
